import { useState } from 'react';
import { X, Mail, Copy, Send, Check, Link, MapPin, Truck, Share2 } from 'lucide-react';
import TransportService from './TransportService';

const LocationShareModal = ({ show, transport, onClose }) => {
  const [activeTab, setActiveTab] = useState('email');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const transportService = new TransportService();

  if (!show || !transport) return null;

  // Generate tracking link using the tracking_token from transport
  const trackingUrl = `${window.location.origin}/track/${transport.tracking_token}`;
  
  const handleCopyLink = async () => {
    // Check if clipboard API is available and we're in a secure context
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(trackingUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.error('Clipboard API failed: ', err);
        // Fall through to fallback method
      }
    }
    
    // Fallback method for browsers that don't support clipboard API or insecure contexts
    try {
      const textArea = document.createElement('textarea');
      textArea.value = trackingUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error('Copy command failed');
      }
    } catch (fallbackErr) {
      console.error('Fallback copy failed: ', fallbackErr);
      alert('Nu s-a putut copia link-ul. Vă rugăm să selectați și copiați manual link-ul.');
    }
  };

  const handleSendEmail = async () => {
    if (!email.trim()) {
      alert('Vă rugăm introduceți adresa de email');
      return;
    }

    setLoading(true);
    try {
      await transportService.sendLocationLink(transport.id, transport.client_id, 'email', {
        email: email.trim(),
        message: message.trim() || `Bună ziua,\n\nPuteți urmări transportul ${transport.id} accesând link-ul de mai jos:\n${trackingUrl}\n\nMulțumim!`
      });
      
      alert('Email trimis cu succes!');
      handleClose(); // Închide modalul după succes
    } catch (error) {
      console.error('Eroare la trimiterea email-ului:', error);
      alert('Eroare la trimiterea email-ului: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setMessage('');
    setActiveTab('email');
    setCopied(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-black/40 via-black/30 to-black/20 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto transform animate-in slide-in-from-bottom-4 duration-500">
        
        {/* Gradient Header */}
        <div className="relative bg-gradient-to-r from-orange-500 via-orange-600 to-red-500 p-6 rounded-t-2xl">
          <div className="absolute inset-0 bg-black/10 rounded-t-2xl"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                <Share2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Partajare Locație
                </h2>
                <p className="text-orange-100 text-sm font-medium">
                  Transport {transport.id}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-all duration-200 group"
            >
              <X className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>

        {/* Transport Info Card */}
        <div className="p-6 bg-gradient-to-br from-gray-50 to-white border-b border-gray-100">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Truck className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-gray-600">Transport</span>
              </div>
              <span className="text-sm font-bold text-gray-900 bg-gray-100 px-3 py-1 rounded-full">
                {transport.id}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Mail className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-sm font-medium text-gray-600">Client</span>
              </div>
              <span className="text-sm font-bold text-gray-900 max-w-40 truncate">
                {transport.client}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MapPin className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-gray-600">Rută</span>
              </div>
              <span className="text-sm font-bold text-gray-900 max-w-40 truncate">
                {transport.route}
              </span>
            </div>
          </div>
        </div>

        {/* Modern Tabs */}
        <div className="flex bg-gray-50 p-2 mx-6 mt-6 rounded-xl">
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 ${
              activeTab === 'email'
                ? 'bg-white text-orange-600 shadow-md transform scale-105'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Mail className="w-4 h-4" />
            <span>Email</span>
          </button>
          <button
            onClick={() => setActiveTab('link')}
            className={`flex-1 px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 ${
              activeTab === 'link'
                ? 'bg-white text-orange-600 shadow-md transform scale-105'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Link className="w-4 h-4" />
            <span>Link Direct</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'email' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  Adresa de email *
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="client@example.com"
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                    required
                  />
                  <Mail className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  Mesaj personalizat (opțional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Bună ziua,\n\nPuteți urmări transportul ${transport.id} accesând link-ul de mai jos:\n${trackingUrl}\n\nMulțumim!`}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none transition-all duration-200 bg-gray-50 focus:bg-white"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={handleClose}
                  className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
                >
                  Anulează
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={loading || !email.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Se trimite...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Trimite Email
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'link' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  Link de urmărire
                </label>
                <div className="flex items-center space-x-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={trackingUrl}
                      readOnly
                      className="w-full pl-4 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 font-mono"
                    />
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className={`px-6 py-3 rounded-xl transition-all duration-300 flex items-center font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 ${
                      copied
                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                        : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                    }`}
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Copiat!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copiază
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-semibold text-blue-800 mb-1">
                      Urmărire în timp real
                    </h4>
                    <p className="text-sm text-blue-700">
                      Acest link permite clientului să urmărească locația vehiculului în timp real și să vadă progresul transportului.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleClose}
                  className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
                >
                  Închide
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationShareModal;